/**
 * WorkflowBuilder — AI-first workflow editor
 * AI edit at top, steps list, collapsible manual add
 */
import { useState, useEffect } from 'react';
import { ACTION_TYPES, TRIGGER_TYPES } from '../constants/workflow.js';

const GROUP_STYLE = {
  Messaging: { bg: 'bg-blue-50', border: 'border-blue-200', icon: '✉️' },
  CRM: { bg: 'bg-green-50', border: 'border-green-200', icon: '👤' },
  Sales: { bg: 'bg-amber-50', border: 'border-amber-200', icon: '💼' },
  AI: { bg: 'bg-violet-50', border: 'border-violet-200', icon: '✨' },
  Flow: { bg: 'bg-rose-50', border: 'border-rose-200', icon: '🔀' },
  Data: { bg: 'bg-cyan-50', border: 'border-cyan-200', icon: '📊' },
  System: { bg: 'bg-gray-50', border: 'border-gray-200', icon: '⚙️' },
  General: { bg: 'bg-indigo-50', border: 'border-indigo-200', icon: '▶️' },
};

function StepCard({ step, index, onRemove, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(step.label || '');
  const [config, setConfig] = useState(step.config || {});
  const actionType = config.action_type || step.step_type;
  const action = ACTION_TYPES.find(a => a.value === actionType) || {};
  const style = GROUP_STYLE[action.group] || GROUP_STYLE.System;

  function handleSave() {
    onUpdate?.(step.id, { label, config });
    setEditing(false);
  }

  function updateConfig(key, value) {
    setConfig(prev => ({ ...prev, [key]: value }));
  }

  return (
    <div className={`rounded-xl border ${style.border} ${style.bg} ${editing ? 'ring-2 ring-indigo-300' : 'cursor-pointer hover:shadow-sm'}`}
      onClick={() => !editing && setEditing(true)}>
      <div className="flex items-start gap-3 p-4">
        <div className="flex-shrink-0 w-7 h-7 rounded-full bg-white border border-gray-200 flex items-center justify-center text-xs font-bold text-gray-500">{index + 1}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm">{style.icon}</span>
            {editing ? (
              <input value={label} onChange={e => setLabel(e.target.value)} className="font-medium text-sm text-gray-900 bg-white border border-gray-200 rounded px-2 py-0.5 flex-1" placeholder="Step name" onClick={e => e.stopPropagation()} />
            ) : (
              <span className="font-medium text-sm text-gray-900">{label || action.label || actionType || 'Step'}</span>
            )}
            {step.requires_approval && <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">Approval</span>}
          </div>
          {!editing && config.prompt && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{config.prompt}</p>}
          {!editing && config.template && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{config.template}</p>}
        </div>
        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
          {editing && <button onClick={handleSave} className="text-xs px-2 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700">Save</button>}
          {editing && <button onClick={() => setEditing(false)} className="text-xs px-2 py-1 text-gray-500 hover:text-gray-700">Cancel</button>}
          {onRemove && <button onClick={() => onRemove(index)} className="text-gray-300 hover:text-red-500 text-sm ml-1">✕</button>}
        </div>
      </div>
      {editing && (
        <div className="px-4 pb-4 space-y-2 border-t border-gray-100 mt-0 pt-3" onClick={e => e.stopPropagation()}>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-medium text-gray-500 uppercase">Action Type</label>
              <select value={config.action_type || ''} onChange={e => updateConfig('action_type', e.target.value)} className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
                {ACTION_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-medium text-gray-500 uppercase">Requires Approval</label>
              <select value={config.requires_approval ? 'yes' : 'no'} onChange={e => updateConfig('requires_approval', e.target.value === 'yes')} className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </div>
          </div>
          {['ai_research', 'ai_compose', 'ai_summarize'].includes(config.action_type) && (
            <div>
              <label className="text-[10px] font-medium text-gray-500 uppercase">Prompt / Template</label>
              <textarea value={config.prompt || config.template || ''} onChange={e => updateConfig(config.action_type === 'ai_compose' ? 'template' : 'prompt', e.target.value)}
                className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 h-20 resize-none" placeholder="Enter prompt or template..." />
            </div>
          )}
          {['send_email'].includes(config.action_type) && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-[10px] font-medium text-gray-500 uppercase">To</label><input value={config.to || ''} onChange={e => updateConfig('to', e.target.value)} className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5" placeholder="recipient@email.com" /></div>
                <div><label className="text-[10px] font-medium text-gray-500 uppercase">Subject</label><input value={config.subject || ''} onChange={e => updateConfig('subject', e.target.value)} className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5" placeholder="Email subject" /></div>
              </div>
            </>
          )}
          {['send_sms'].includes(config.action_type) && (
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[10px] font-medium text-gray-500 uppercase">To</label><input value={config.to || ''} onChange={e => updateConfig('to', e.target.value)} className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5" placeholder="+1234567890" /></div>
              <div><label className="text-[10px] font-medium text-gray-500 uppercase">Message</label><input value={config.body || ''} onChange={e => updateConfig('body', e.target.value)} className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5" placeholder="SMS body" /></div>
            </div>
          )}
          {['wait'].includes(config.action_type) && (
            <div><label className="text-[10px] font-medium text-gray-500 uppercase">Delay (minutes)</label><input type="number" value={config.delay_minutes || 0} onChange={e => updateConfig('delay_minutes', parseInt(e.target.value))} className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5" /></div>
          )}
          {['for_each'].includes(config.action_type) && (
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[10px] font-medium text-gray-500 uppercase">Items Source</label><input value={config.items_ref || ''} onChange={e => updateConfig('items_ref', e.target.value)} className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5" placeholder="e.g. results" /></div>
              <div><label className="text-[10px] font-medium text-gray-500 uppercase">Child Action</label>
                <select value={config.child_action_type || ''} onChange={e => updateConfig('child_action_type', e.target.value)} className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white">
                  <option value="">Select...</option>
                  {ACTION_TYPES.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
            </div>
          )}
          {['tally_to_sheet'].includes(config.action_type) && (
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[10px] font-medium text-gray-500 uppercase">Data Source</label><input value={config.source_ref || ''} onChange={e => updateConfig('source_ref', e.target.value)} className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5" placeholder="e.g. responses" /></div>
              <div><label className="text-[10px] font-medium text-gray-500 uppercase">Sheet Name</label><input value={config.sheet_name || ''} onChange={e => updateConfig('sheet_name', e.target.value)} className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5" placeholder="Report Name" /></div>
            </div>
          )}
          {['notify_user'].includes(config.action_type) && (
            <div><label className="text-[10px] font-medium text-gray-500 uppercase">Message</label><input value={config.message || ''} onChange={e => updateConfig('message', e.target.value)} className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5" placeholder="Notification message" /></div>
          )}
        </div>
      )}
    </div>
  );
}

export default function WorkflowBuilder({ workflowId, onBack }) {
  const [workflow, setWorkflow] = useState(null);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);
  const [showAddStep, setShowAddStep] = useState(false);

  const isNew = !workflowId || workflowId === 'new';

  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    fetch(`/api/workflows/${workflowId}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        const wf = data.data?.workflow || data.workflow || data;
        setWorkflow(wf);
        setSteps(wf.steps || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [workflowId, isNew]);

  async function handleGenerate() {
    if (!aiPrompt.trim()) return;
    setGenerating(true);
    setError(null);
    try {
      const context = !isNew && steps.length > 0
        ? '\n\nExisting workflow: ' + (workflow?.name || '') + '\nCurrent steps: ' + JSON.stringify(steps.map(s => ({ type: s.config?.action_type, label: s.label })))
        : '';
      const res = await fetch('/api/workflows/generate', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: aiPrompt + context, name: workflow?.name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate');
      const generated = data.data?.workflow || data.workflow || data;

      if (!isNew && workflow?.id && generated.steps?.length > 0) {
        // Replace steps on existing workflow
        for (const s of steps) {
          await fetch(`/api/workflows/${workflow.id}/steps/${s.id}`, { method: 'DELETE', credentials: 'include' }).catch(() => {});
        }
        const newSteps = [];
        for (const step of generated.steps) {
          const res2 = await fetch(`/api/workflows/${workflow.id}/steps`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ step_type: step.step_type || 'action', config: step.config || {}, position: newSteps.length, label: step.label || '', requires_approval: step.requires_approval || false }),
          });
          const sd = await res2.json();
          const ns = sd.data?.step || sd.step;
          if (ns) newSteps.push(ns);
        }
        setSteps(newSteps);
      } else {
        setWorkflow(generated);
        setSteps(generated.steps || []);
        setSaved(true);
      }
      setAiPrompt('');
    } catch (e) { setError(e.message); }
    finally { setGenerating(false); }
  }

  async function handleUpdateStep(stepId, updates) {
    const wfId = workflowId || workflow?.id;
    if (!wfId || !stepId) return;
    try {
      await fetch(`/api/workflows/${wfId}/steps/${stepId}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      setSteps(prev => prev.map(s => s.id === stepId ? { ...s, ...updates } : s));
    } catch (e) { setError(e.message); }
  }

  async function handleRemoveStep(index) {
    const step = steps[index];
    const wfId = workflowId || workflow?.id;
    if (!wfId || !step?.id) return;
    try {
      await fetch(`/api/workflows/${wfId}/steps/${step.id}`, { method: 'DELETE', credentials: 'include' });
      setSteps(prev => prev.filter((_, i) => i !== index));
    } catch (e) { setError(e.message); }
  }

  async function handleAddStep(actionType) {
    const wfId = workflowId || workflow?.id;
    if (!wfId) return;
    try {
      const action = ACTION_TYPES.find(a => a.value === actionType) || {};
      const res = await fetch(`/api/workflows/${wfId}/steps`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ step_type: 'action', config: { action_type: actionType }, position: steps.length, label: action.label || actionType }),
      });
      const data = await res.json();
      const ns = data.data?.step || data.step;
      if (ns) setSteps(prev => [...prev, ns]);
      setShowAddStep(false);
    } catch (e) { setError(e.message); }
  }

  async function handleRun() {
    const wfId = workflowId || workflow?.id;
    if (!wfId) return;
    try {
      if (workflow?.status === 'draft') await fetch(`/api/workflows/${wfId}/activate`, { method: 'PATCH', credentials: 'include' });
      const res = await fetch(`/api/workflows/${wfId}/execute`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const data = await res.json();
      if (data.success || data.data) { alert('Workflow started!'); if (onBack) onBack(); }
      else setError(data.error || 'Failed to start');
    } catch (e) { setError(e.message); }
  }

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full" /></div>;

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {onBack && <button onClick={onBack} className="text-gray-400 hover:text-gray-600 text-sm">← Back</button>}
          <h1 className="text-xl font-bold text-gray-900">{workflow?.name || 'New Workflow'}</h1>
          {workflow?.status && <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">{workflow.status}</span>}
        </div>
        {steps.length > 0 && (
          <button onClick={handleRun} className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700">▶ Run</button>
        )}
      </div>

      {/* AI Section — always visible */}
      <div className="mb-6 bg-gradient-to-br from-indigo-50 to-violet-50 rounded-2xl border border-indigo-200 p-5">
        <div className="flex items-center gap-2 mb-2">
          <span>✨</span>
          <h2 className="text-sm font-semibold text-gray-900">{isNew ? 'Describe your workflow' : 'Edit with AI'}</h2>
        </div>
        <textarea
          value={aiPrompt}
          onChange={e => setAiPrompt(e.target.value)}
          placeholder={isNew
            ? 'Example: Research 10 law firms in Indiana, draft personalized emails to each asking for quotes, wait for responses, compile results into a spreadsheet.'
            : "Describe changes: e.g. 'Add a follow-up SMS step' or 'Remove the wait step and add an approval gate before sending'"}
          className="w-full h-24 px-3 py-2.5 rounded-xl border border-indigo-200 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white"
          onKeyDown={e => { if (e.key === 'Enter' && e.metaKey) handleGenerate(); }}
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-[11px] text-gray-400">⌘+Enter to submit</span>
          <button onClick={handleGenerate} disabled={generating || !aiPrompt.trim()}
            className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2">
            {generating ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> Working...</> : <>✨ {isNew ? 'Generate' : 'Update'} Workflow</>}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between">
          {error} <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Steps */}
      {steps.length > 0 && (
        <div className="mb-6">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Steps ({steps.length})</h3>
          <div className="space-y-2">
            {steps.map((step, i) => <StepCard key={step.id || i} step={step} index={i} onUpdate={handleUpdateStep} onRemove={handleRemoveStep} />)}
          </div>
        </div>
      )}

      {/* Add Step — collapsed by default */}
      {(workflowId && workflowId !== 'new' || workflow?.id) && (
        <div className="border-t border-gray-100 pt-4">
          <button onClick={() => setShowAddStep(!showAddStep)}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1">
            <span className={`transition-transform ${showAddStep ? 'rotate-90' : ''}`}>▸</span> Add step manually
          </button>
          {showAddStep && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {ACTION_TYPES.map(a => {
                const s = GROUP_STYLE[a.group] || GROUP_STYLE.System;
                return (
                  <button key={a.value} onClick={() => handleAddStep(a.value)}
                    className={`px-2.5 py-1 rounded-lg border text-[11px] font-medium ${s.bg} ${s.border} text-gray-600 hover:shadow-sm`}>
                    {s.icon} {a.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {isNew && !saved && steps.length === 0 && !generating && (
        <div className="text-center py-8 text-gray-300">
          <p className="text-4xl mb-3">✨</p>
          <p className="text-sm">Describe what you want above and AI will build it</p>
        </div>
      )}
    </div>
  );
}
